import { useTranslation } from "react-i18next";
import { FacebookOutlined, MailOutlined, MessageOutlined, PhoneOutlined, RightOutlined } from "@ant-design/icons";
import useCommonSettings from "@base/hooks/useCommonSettings";
import { hotlineHref } from "@base/modules/appointment/appointmentDetailRules";
import CommonStyles from "@components/CommonStyles";

interface IProps {
  open: boolean;
  onClose: () => void;
}

/** The ways to reach support, listing only the channels that are configured. */
export default function ContactHelpModal({ open, onClose }: IProps) {
  //! state
  const { t } = useTranslation();
  const { hotline, facebook, zalo, email } = useCommonSettings();

  const channels = [
    { key: "hotline", icon: <PhoneOutlined />, label: t("appointmentDetail.contactHotline"), detail: hotline, href: hotlineHref(hotline) },
    { key: "messenger", icon: <FacebookOutlined />, label: t("appointmentDetail.contactMessenger"), detail: "", href: facebook && `https://m.me/${facebook}` },
    { key: "zalo", icon: <MessageOutlined />, label: t("appointmentDetail.contactZalo"), detail: "", href: zalo && `https://zalo.me/${zalo}` },
    { key: "email", icon: <MailOutlined />, label: t("appointmentDetail.contactEmail"), detail: email, href: email && `mailto:${email}` },
  ].filter((channel) => !!channel.href);

  //! render
  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={onClose}
      type="normal"
      width={440}
      title={t("appointmentDetail.howToContact")}
      footer={
        <CommonStyles.ButtonUI className="w-fit h-10 px-5" type="primary" onClick={onClose}>
          {t("Close")}
        </CommonStyles.ButtonUI>
      }
      content={
        <div className="flex flex-col gap-2">
          {channels.map((channel) => (
            <a
              key={channel.key}
              href={channel.href}
              target={channel.key === "hotline" || channel.key === "email" ? undefined : "_blank"}
              rel="noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg border border-solid border-common-divider !text-common-maintext hover:border-common-primary hover:bg-common-primary50 transition-colors"
            >
              <span className="flex-none flex text-lg text-common-primary">{channel.icon}</span>
              <span className="grow min-w-0 flex flex-col">
                <span className="text-sm font-medium font-['Inter']">{channel.label}</span>
                {channel.detail ? <span className="text-xs font-['Inter'] text-common-subtext break-all">{channel.detail}</span> : null}
              </span>
              <RightOutlined className="flex-none text-xs text-common-subtext" />
            </a>
          ))}
        </div>
      }
    />
  );
}
